import * as Sentry from '@sentry/vue';

const paymentIntegrationsUrl = import.meta.env.VITE_PAYMENT_INTEGRATIONS_URL;
const subsMgmtV2Url = import.meta.env.VITE_SUBS_MGMT_V2_URL;

// Converts a Recurly plan code like "speedify-dedicated-server-yearly"
// into a display name like "Speedify Dedicated Server Yearly".
export const formatPlanCode = (code) =>
  String(code || '')
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const generateRecaptchaV3Token = async () => {
  return new Promise((resolve) => {
    grecaptcha.ready(async () => {
      try {
        const token = await grecaptcha.execute(import.meta.env.VITE_RECAPTCHA_KEY, { action: 'submit' });
        resolve({ token });
      } catch (err) {
        Sentry.captureException(err);
        resolve({ error: { message: 'Failed to generate reCAPTCHA token' } });
      }
    });
  });
};

export const createPurchase = async (checkoutData, turnstile, tokenId, analytics, paymentType) => {
  const email = typeof checkoutData.email === 'string' ? checkoutData.email.trim() : '';
  if (!email) {
    return { error: { message: 'Email is required' } };
  }

  const data = {
    currency: checkoutData.currency,
    email,
    plan: checkoutData.plan,
    quantity: checkoutData.quantity || 1,
    coupon: checkoutData.coupon.validatedCode,
    analytics,
    paymentType, // paypal or credit_card
    ...(checkoutData.company && { company: checkoutData.company }),
  };

  if (paymentType === 'credit_card') {
    const recaptchaResult = await generateRecaptchaV3Token();
    if (recaptchaResult.error) {
      return recaptchaResult;
    }
    data.recaptchaV3 = recaptchaResult.token;
    data.captchaProvider = 'turnstile';
    data.captchaResponse = turnstile;
  }

  // TODO: set these as conditional properties in data
  if (checkoutData.servers) {
    data.servers = {
      planCode: checkoutData.serverCode,
      quantity: checkoutData.servers,
    };
  }

  if (checkoutData.vatNumber) {
    data.vatNumber = checkoutData.vatNumber;
  }

  if (tokenId) {
    data.tokenId = tokenId;
  }

  try {
    const response = await fetch(`${paymentIntegrationsUrl}/cart/subscriptions/new`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    const body = await response.json().catch(err => {
      // parse failure on 200 is not an error from our side and not actionable
      if (!response.ok) {
        Sentry.captureException(err, {
          tags: { source: 'createPurchase.jsonParse' },
          extra: { status: response.status },
        });
      }
      return null;
    });
    if (!response.ok || body == null || body?.error) {
      return { error: normalizePurchaseError(body, response.status) };
    }
    return body;
  } catch {
    return { error: { message: 'Network error. Please check your connection and try again.' } };
  }
};

// payment-integrations may return error as a string, an object with `.message`,
// or an object with a `.messages` array of `{ err }` items. Collapse into a single
// `{ message, errorCode }` shape so consumers can just read `error.message`.
//
// The PayPal alternate-plan path surfaces a `{ text, alternatePlanCode }` object
// on `message` so PaymentErrorAlert can render the link to a non-renewing plan.
const normalizePurchaseError = (body, status) => {
  const fallback = `Purchase failed (HTTP ${status}). Please try again or contact support@speedify.com.`;
  const err = body?.error;
  if (typeof err === 'string') return { message: err || fallback };
  if (err && typeof err === 'object') {
    const paypalErrorMessage = Array.isArray(err.messages)
      && err.messages.find(m => m?.paypalErrorMessage)?.paypalErrorMessage;
    if (paypalErrorMessage) {
      return { message: paypalErrorMessage, errorCode: err.errorCode };
    }
    const fromMessages = Array.isArray(err.messages)
      && err.messages.map(m => m?.err || m?.message || (typeof m === 'string' ? m : '')).filter(Boolean).join('; ');
    return { message: fromMessages || err.message || fallback, errorCode: err.errorCode };
  }
  return { message: fallback };
};

export const getCoupon = async couponCode => {
  try {
    const response = await fetch(`${paymentIntegrationsUrl}/cart/coupons/get/${encodeURIComponent(couponCode)}`);
    return await response.json();
  } catch {
    // network/parse failure — distinguish from a genuinely invalid coupon so the
    // UI can show a transient-error message instead of "Invalid coupon code"
    return { error: true, networkError: true };
  }
};

// to be deprecated when recurlyJS can catch invalid plan codes on its own
export const checkPlan = async planCode => {
  try {
    const response = await fetch(`${paymentIntegrationsUrl}/cart/plans/check/${encodeURIComponent(planCode)}`);
    return response;
  } catch {
    return { error: { message: 'Failed to check plan.' } };
  }
};

export const verifyTokenUser = async () => {
  try {
    const response = await fetch(`${subsMgmtV2Url}/api/user`, { credentials: 'include' });
    return response;
  } catch {
    return { error: { message: 'Failed to verify user session.' } };
  }
};

export const logoutFromAccount = async () => {
  try {
    // invalidates session and token cookies
    await fetch(
      `${subsMgmtV2Url}/logout`,
      { method: 'GET', withCredentials: true, credentials: 'include' }
    );
    return { success: true };
  } catch {
    return { error: { message: 'Failed to log out.' } };
  }
};

export const getUserSubscriptions = async () => {
  try {
    const response = await fetch(`${subsMgmtV2Url}/api/account/subscriptions`,
      {
        method: 'GET',
        withCredentials: true,
        credentials: 'include',
      },
    );
    const result = await response.json();
    return result;
  } catch {
    return { error: { message: 'Failed to load subscriptions.' } };
  }
};
